import { useState } from "react";
import { Link } from "react-router-dom";
import SEOHead from "@/components/feature/SEOHead";
import { OrganizationSchema, WebPageSchema } from "@/components/feature/SchemaOrg";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { siteInfo } from "@/mocks/articles";

export default function AboutPage() {
  const stats = [
    { value: "10+", label: "深度评测文章", icon: "ri-article-line" },
    { value: "4", label: "视频平台对比", icon: "ri-apps-2-line" },
    { value: "10万+", label: "月度活跃读者", icon: "ri-user-heart-line" },
    { value: "99%", label: "读者满意度", icon: "ri-emotion-happy-line" },
  ];

  const values = [
    {
      icon: "ri-shield-check-line",
      title: "客观公正",
      desc: "所有评测基于真实数据和实际使用体验，不受任何平台商业合作影响，确保评测结果的客观公正性。",
    },
    {
      icon: "ri-search-eye-line",
      title: "深度专业",
      desc: "从资源数量、播放流畅度、免费高清画质、使用体验等多维度进行全方位对比评测，让用户全面了解各平台优劣。",
    },
    {
      icon: "ri-user-voice-line",
      title: "用户至上",
      desc: "以用户真实需求为导向，结合大量网评口碑数据，为用户提供最实用的视频平台选择建议和推荐。",
    },
    {
      icon: "ri-refresh-line",
      title: "持续更新",
      desc: "评测内容持续更新，紧跟各视频平台的最新版本和功能变化，确保推荐信息始终准确有效。",
    },
  ];

  return (
    <>
      <SEOHead
        title="关于我们 - 蘑菇视频平台评测 | 品牌介绍与口碑"
        description="蘑菇视频专注于视频平台横向对比评测，提供蘑菇视频、樱花视频、樱桃视频、糖豆视频等平台的客观评测与推荐，帮助用户找到最好用的视频平台。了解我们的品牌故事和评测理念。"
        keywords="蘑菇视频,关于我们,品牌介绍,视频评测,口碑"
        canonicalUrl="https://mogutv.com/about"
      />
      <OrganizationSchema />
      <WebPageSchema
        title="关于我们 - 蘑菇视频平台评测"
        description={siteInfo.description}
        url="https://mogutv.com/about"
        breadcrumbs={[
          { name: "首页", url: "https://mogutv.com" },
          { name: "关于我们", url: "https://mogutv.com/about" },
        ]}
      />

      <main className="pt-20">
        {/* Header */}
        <section className="w-full bg-background-100/70 border-b border-background-200/60">
          <div className="w-full px-4 md:px-6 py-10">
            <div className="mb-4">
              <Breadcrumbs
                items={[
                  { label: "首页", href: "/" },
                  { label: "关于我们" },
                ]}
              />
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              关于蘑菇视频
            </h1>
            <p className="text-sm text-foreground-600 max-w-xl">
              {siteInfo.tagline} — {siteInfo.description}
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full bg-background-50 py-14 md:py-18">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 items-center">
              <div className="lg:w-1/2">
                <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-4">
                  品牌故事
                </span>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  我们为什么做视频平台评测？
                </h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                  在视频平台百花齐放的今天，用户面临着蘑菇视频、樱花视频、樱桃视频、糖豆视频等众多选择。每个平台都有各自的优势和不足，但网上的信息零散且真假难辨。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                  蘑菇视频评测团队由此诞生。我们汇聚了一批热爱观影、熟悉技术的资深用户，通过系统化、多维度的实际测试，为用户提供最真实、最客观的视频平台对比评测。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  我们相信，好的评测应该让用户看得懂、用得上。因此我们坚持用通俗易懂的语言，从用户最关心的资源数量、播放流畅度、免费高清画质等维度出发，帮大家找到最好用的视频平台。
                </p>
              </div>
              <div className="lg:w-1/2 w-full">
                <div className="w-full h-72 md:h-80 rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20cozy%20home%20theater%20setup%20with%20large%20screen%20displaying%20movie%20content%2C%20comfortable%20sofa%20with%20throw%20pillows%2C%20warm%20ambient%20lighting%20from%20floor%20lamp%2C%20wooden%20coffee%20table%20with%20popcorn%2C%20cinematic%20atmosphere%2C%20editorial%20interior%20photography%2C%20rich%20warm%20coral%20and%20amber%20tones&width=700&height=500&seq=mogutv-about-story&orientation=landscape"
                    alt="蘑菇视频品牌故事 - 温馨家庭影院场景"
                    className="w-full h-full object-cover"
                    title="蘑菇视频品牌故事"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full bg-background-100/70 py-12">
          <div className="w-full px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-4">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-primary-100 rounded-lg">
                    <i className={`${stat.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{stat.value}</div>
                  <p className="text-xs text-foreground-600 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="w-full bg-background-50 py-14 md:py-18">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full mb-3">
                评测理念
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                我们的评测标准
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                坚持四大核心原则，确保每篇评测都值得信赖
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
              {values.map((v) => (
                <div key={v.title} className="bg-background-100/80 rounded-lg p-6 border border-background-200/60 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-accent-100 rounded-full">
                    <i className={`${v.icon} text-accent-600 text-xl`}></i>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-950 mb-2">{v.title}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="w-full bg-background-100/70 py-14 md:py-18">
          <div className="w-full px-4 md:px-6 max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600">
                有任何问题或建议？欢迎通过以下方式联系我们
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className="bg-background-50 rounded-lg p-5 border border-background-200/60 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-primary-100 rounded-lg">
                  <i className="ri-mail-line text-primary-600 text-lg"></i>
                </div>
                <h3 className="text-sm font-heading font-bold text-foreground-950 mb-1">电子邮箱</h3>
                <p className="text-xs text-foreground-600">contact@mogutv.com</p>
              </div>
              <div className="bg-background-50 rounded-lg p-5 border border-background-200/60 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-accent-100 rounded-lg">
                  <i className="ri-wechat-line text-accent-600 text-lg"></i>
                </div>
                <h3 className="text-sm font-heading font-bold text-foreground-950 mb-1">微信公众号</h3>
                <p className="text-xs text-foreground-600">蘑菇视频官方</p>
              </div>
              <div className="bg-background-50 rounded-lg p-5 border border-background-200/60 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-secondary-100 rounded-lg">
                  <i className="ri-earth-line text-secondary-600 text-lg"></i>
                </div>
                <h3 className="text-sm font-heading font-bold text-foreground-950 mb-1">官方网站</h3>
                <p className="text-xs text-foreground-600">www.mogutv.com</p>
              </div>
            </div>

            <ContactForm />
          </div>
        </section>
      </main>
    </>
  );
}

function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const FORM_URL = "https://readdy.ai/api/form/d9mtt4gbdkpk99j7ge10";

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");

    try {
      const formEl = e.currentTarget;
      const honeypotEl = formEl.querySelector<HTMLInputElement>('input[name="phone_alt"]');
      if (honeypotEl && honeypotEl.value.trim() !== "") {
        setStatus("success");
        formEl.reset();
        return;
      }

      const formData = new FormData(formEl);
      formData.delete("phone_alt");

      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === "OK") {
        setStatus("success");
        formEl.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || responseText || "提交失败，请稍后再试。";
        throw new Error(msg);
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "提交失败，请稍后再试。");
    }
  };

  return (
    <div className="bg-background-50 rounded-lg p-6 border border-background-200/60">
      <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4 text-center">发送消息</h3>
      <form onSubmit={handleSubmit} data-readdy-form="">
        <input
          type="text"
          name="phone_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="form-honeypot"
        />
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="text"
              name="name"
              placeholder="您的姓名"
              required
              className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
            />
            <input
              type="email"
              name="email"
              placeholder="您的邮箱"
              required
              className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
            />
          </div>
          <input
            type="text"
            name="subject"
            placeholder="主题"
            required
            className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
          />
          <textarea
            name="message"
            placeholder="请输入您的留言内容..."
            rows={4}
            required
            maxLength={500}
            className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
          ></textarea>
          <button
            type="submit"
            disabled={status === "submitting"}
            className="w-full py-2.5 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap"
          >
            {status === "submitting" ? "发送中..." : "发送消息"}
          </button>
          {status === "success" && (
            <p className="text-xs text-center text-green-600">消息已成功发送，我们会尽快回复您！</p>
          )}
          {status === "error" && (
            <p className="text-xs text-center text-red-500">{errorMsg}</p>
          )}
        </div>
      </form>
    </div>
  );
}